import type {
    CurationErrorCode,
    CurationStatus,
    DocumentProcessingStatus,
    ExtractionMethod,
    PipelineStage,
} from '@packages/enums';

export type ResultLevel = 'patient' | 'document';
export type ResultStatus = 'completed' | 'failed' | 'partial';

// ============================================================
// Progress Tracking Types (Shared between frontend and backend)
// ============================================================

/**
 * Document status detail for progress tracking
 * Used in both API responses and SSE events
 */
export interface DocumentStatusDetail {
    id: string;
    document_id: string;
    patient_id?: number;
    patient_name?: string;
    status: DocumentProcessingStatus | string;
    stage?: PipelineStage | string;
    stage_status?: string;
    started_at: Date | string | null;
    completed_at: Date | string | null;
    updated_at?: Date | string;
    error: string | null;
    processing_time_ms: number | null;
    session_id?: string;
}

/**
 * Per-patient/session progress for patient-level extraction
 */
export interface PatientStageProgress {
    session_id: string;
    patient_id?: number;
    status: CurationStatus | string;
    progress: number;
    stage_details?: StageDetails;
    message?: string;
    error?: string;
    document_count: number;
}

/**
 * Structured error information for debugging
 */
export interface SessionError {
    sessionId: string;
    patientId?: string | number;
    error: string;
    stage?: PipelineStage | string;
    timestamp: string;
}

/**
 * Document-level error information
 */
export interface DocumentError {
    documentId: string;
    patientId?: number;
    error: string;
    stage?: PipelineStage | string;
    timestamp: string;
}

/**
 * Aggregated error details for a curation job
 */
export interface CurationError {
    code: CurationErrorCode | string;
    message: string;
    timestamp: string;
    sessionErrors: SessionError[];
    documentErrors: DocumentError[];
}

/**
 * Unified progress update sent via SSE
 * Used by both job list and job detail views
 */
export interface CurationProgressUpdate {
    jobId: string;
    status: CurationStatus | string;
    progress: number;
    message?: string;
    stage?: string;
    document_stats: DocumentStats;
    total_documents: number;
    completed_documents: number;
    failed_documents: number;
    processing_documents?: number;
    document_statuses: DocumentStatusDetail[];
    patient_stages?: PatientStageProgress[];
    sessions_count?: number;
    sessions_completed?: number;
    sessions_failed?: number;
    timing?: TimingInfo;
    error?: CurationError | string | null;
}

/**
 * Job state tracked by progress state service
 */
export interface JobProgressState {
    jobId: string;
    status: CurationStatus | string;
    progress: number;
    extractionMethod: ExtractionMethod | string;
    totalDocuments: number;
    sessions: Map<string, SessionStatus> | Record<string, SessionStatus>;
    documents: Map<string, DocumentStatusDetail> | Record<string, DocumentStatusDetail>;
    error?: CurationError;
    updatedAt: Date;
}

/**
 * Session status within a job
 */
export interface SessionStatus {
    session_id: string;
    patient_id?: number;
    status: string;
    progress: number;
    stage_details?: StageDetails;
    timing?: TimingInfo;
    message?: string;
    error?: string;
    document_ids: string[];
}

/**
 * Document-level status information for data curation progress tracking
 */
export interface DocumentStatus {
    id: string;
    document_id: string;
    patient_id?: number;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    stage?: string;
    started_at: string | null;
    completed_at: string | null;
    updated_at?: string;
    error: string | null;
    processing_time_ms?: number;
}

/**
 * Per-file status tracking with stage information
 */
export interface FileStatus {
    file_path: string;
    stage: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    message?: string;
    error?: string;
    file_completed: boolean;
    updated_at: string;
}

/**
 * Stage-level progress information
 */
export interface StageProgress {
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
}

/**
 * Detailed progress for all pipeline stages
 */
export interface StageDetails {
    current_stage: string;
    stage_1_tagger?: StageProgress;
    stage_2_extractor?: StageProgress;
    stage_3_consolidator?: StageProgress;
}

/**
 * Document statistics counters
 */
export interface DocumentStats {
    total_documents: number;
    documents_processed: number;
    documents_pending: number;
    documents_failed: number;
}

/**
 * File-level progress metrics
 */
export interface FileProgress {
    completed_files: number;
    total_files: number;
    progress_percentage: number;
}

/**
 * Timing information for progress estimation
 */
export interface TimingInfo {
    elapsed_seconds: number;
    estimated_total_seconds?: number;
    estimated_remaining_seconds?: number;
}

/**
 * Status update from data curation SSE stream
 */
export interface DataCurationStatusResponse {
    status: 'queued' | 'processing' | 'completed' | 'failed';
    session_id: string;
    progress?: number;
    message?: string;
    stage?: string;
    error?: string;
    task_id?: string;

    // Stage-level tracking
    stage_details?: StageDetails;

    // Document statistics
    document_stats?: DocumentStats;

    // Legacy document-level tracking (for backward compatibility)
    total_documents?: number;
    completed_documents?: number;
    failed_documents?: number;

    // File-level tracking
    file_status?: FileStatus[];
    file_progress?: FileProgress;

    // Document statuses from SSE (with patient_id)
    document_statuses?: DocumentStatus[];

    // Timing information
    timing?: TimingInfo;
    elapsed_seconds?: number;

    // Metadata
    created_at?: string;
    updated_at?: string;
}

/**
 * Initial response from data curation service when job is submitted
 */
export interface DataCurationJobResponse {
    status: 'queued' | 'processing';
    session_id: string;
    download_url: string;
    websocket_url: string;
    processing_time_seconds: number | null;
}

/**
 * Paginated response for document statuses
 */
export interface DocumentStatusListResponse {
    documents: DocumentStatus[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

/**
 * Interim extraction result from Elasticsearch
 * Contains the extracted data before final processing
 */
export interface InterimResult {
    session_id: string;
    document_id: string;
    filename: string | null;
    doc_type: string | null;
    doc_date: string | null;
    level: string;
    results: {
        doc_id: string;
        filename: string | null;
        doc_type: string | null;
        doc_date: string | null;
        extracted_fields: unknown[];
        [key: string]: unknown;
    };
    timestamp: string;
}

/**
 * Raw document content from OMOP note table
 */
export interface RawDocumentContent {
    content: string;
    title: string | null;
    noteType: string | null;
    noteDate: string | null;
}

/**
 * Individual document result within document-level consolidation
 */
export interface ConsolidatedDocumentResult {
    doc_id: string;
    patient_id: string | null;
    filename: string | null;
    doc_type: string | null;
    doc_date: string | null;
    extracted_fields: DocumentExtractedField[];
    total_fields_extracted: number;
    processing_time_seconds: number;
}

/**
 * Patient-level consolidation result from Elasticsearch
 */
export interface PatientLevelConsolidationResult {
    session_id: string;
    patient_id: string | null;
    level: 'patient_level';
    results: ConsolidationResult;
    timestamp: string;
}

export interface ConsolidationResult {
    session_id: string;
    total_fields_consolidated: number;
    consolidated_fields: ConsolidationField[];
    consolidated_cancers: ConsolidationCancer[];
    processing_time_seconds: number;
    timestamp: string;
}

export interface ConsolidationField {
    field_name: string;
    category: string;
    consolidated_value: any;
    all_values: any;
    supporting_evidence: Evidence[];
    contradictory_evidence: Evidence[];
    confidence_score: number;
    source_documents: string[];
    consolidation_reasoning: string;
    data_type: string;
    cancer_id?: string;
    /** True if the field value was edited by a user (different from AI extraction) */
    is_user_edited?: boolean;
    /** The original value extracted by the AI before user edits */
    original_ai_value?: any;
}

export interface Evidence {
    snippet: string;
    explanation: string;
    source_file: string;
    confidence: number;
    date: string;
}

export interface ConsolidationCancer {
    cancer_id: string;
    primary_site: string;
    histology: string;
    diagnosis_date: string;
    laterality: any;
    source_docs: string[];
    confidence_level: string;
    registry_ready: boolean;
    fields: CancerField[];
}

export interface CancerField extends ConsolidationField {
    units: any;
    vocabulary_code?: string;
    timeline: any;
}

export interface DocumentExtractedField {
    field_name: string;
    category: string;
    raw_value?: string;
    normalized_value?: string;
    units: any;
    vocabulary_code?: string;
    field_evidence: FieldEvidence;
    confidence_level: string;
    confidence_score: number;
    sources: Source[];
    extraction_timestamp: string;
    data_type: string;
}

export interface FieldEvidence {
    explanation: string;
    inferred: boolean;
    related_entities: string[];
}

export interface Source {
    doc_id: string;
    split_number: number;
    reasoning_excerpt: string;
}

/**
 * Document-level consolidation result from Elasticsearch
 */
export interface DocumentLevelConsolidationResult {
    session_id: string;
    patient_id: string | null;
    level: 'document_level';
    results: {
        session_id: string;
        total_documents_processed: number;
        document_results: ConsolidatedDocumentResult[];
        total_fields_extracted: number;
        processing_time_seconds: number;
        timestamp: string;
    };
    timestamp: string;
}

/**
 * Combined patient consolidation results from Elasticsearch data_curation_results index
 */
export interface PatientConsolidationResults {
    patientLevel: PatientLevelConsolidationResult | null;
    documentLevel: DocumentLevelConsolidationResult | null;
}

export interface PatientDocuments {
    person_id: number;
    document_ids: string[];
    patient_name: string;
}
